import React from 'react';
import {
  INITIAL_INTERESTED_PRODUCT_LINE,
  InterestedProductLine,
  RequiredSubInfoPresenter,
  SubscriberPresenter,
  SubscriberProductLineType,
} from 'types/electionService';
import {
  getDependentOnProductLineTypeFromInterest,
  getInterestedProductLineByProductLineId,
  getPLineProductType,
} from 'global/utils/election/productLineElection';
import { checkProductLineRateValidity, getFormIdForProductLine } from 'global/utils/election/productLineValidation';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import InterestProductLineCard from './ProductLineInterestCard';
import { SubscriberProductLinesStyled } from './styles';
import { SubscriberInterestCardCallbacks } from '../types';

interface Props {
  subscriber: SubscriberPresenter;
  productLineIds: ProductLineId[];
  callbacksMap: SubscriberInterestCardCallbacks;
  isEditable: boolean;
  requiredSubInfo?: RequiredSubInfoPresenter;
}

interface ProductLineChoiceInfo {
  currentInterestedProductLine: InterestedProductLine;
  productLineType: SubscriberProductLineType;
  isValid: boolean;
  showInterestSelectionButton: boolean;
}

const SubscriberInterestCardList: React.FC<Props> = (props) => {
  const {
    subscriber: { id: subId, employeeClass, common, custom, productLineToCarrier, interestedProductLines },
    isEditable,
    callbacksMap,
    requiredSubInfo,
    productLineIds,
  } = props;
  const getProductLineInfo = (productLineId: ProductLineId): ProductLineChoiceInfo => {
    const currentInterestedProductLine = getInterestedProductLineByProductLineId(
      interestedProductLines,
      productLineId,
    ) || { ...INITIAL_INTERESTED_PRODUCT_LINE, productLineId };

    const productLineType = getPLineProductType(currentInterestedProductLine.value);

    const dependentOnProductLineType = getDependentOnProductLineTypeFromInterest(
      productLineId,
      interestedProductLines,
      requiredSubInfo,
    );

    const { errorMessageTypes, showActionButton } = checkProductLineRateValidity(
      common,
      {
        productLineId,
        employeeClass,
        carrierId: productLineToCarrier[productLineId] || '',
        // Products are not available in the interest-only product line flow.
        // An empty string is used to indicate that no product is selected,
        // so product-related required field validation can be skipped.
        productId: '',
        productLineType,
        dependentOnProductLineType,
      },
      requiredSubInfo,
    );

    return {
      currentInterestedProductLine,
      productLineType,
      isValid: errorMessageTypes.length === 0,
      showInterestSelectionButton: isEditable && showActionButton && !!callbacksMap?.onProductLineAction,
    };
  };

  return (
    <SubscriberProductLinesStyled>
      {productLineIds.map((productLineId) => {
        const { productLineType, isValid, showInterestSelectionButton, currentInterestedProductLine } =
          getProductLineInfo(productLineId);

        return (
          <InterestProductLineCard
            key={`${subId}_${productLineId}`}
            subscriberId={subId}
            productLineType={productLineType}
            isValid={isValid}
            dependents={custom.dependents}
            isEditable={isEditable}
            currentInterestSelection={currentInterestedProductLine}
            formId={getFormIdForProductLine(productLineId, productLineType, requiredSubInfo)}
            showInterestSelectionButton={showInterestSelectionButton}
            onDependentsChange={callbacksMap?.onDependentsChange}
            onProductLineInterestChange={callbacksMap?.onProductLineInterestChange}
            onProductLineAction={callbacksMap?.onProductLineAction}
          />
        );
      })}
    </SubscriberProductLinesStyled>
  );
};

export default SubscriberInterestCardList;
